// X3DH oturum kurulumu — saf JS (9.34 · D-605).
//
// İki cihazın, biri çevrimdışıyken bile, ortak bir gizli anahtarı türetmesi.
// Signal'in X3DH'i: dört Diffie-Hellman'ı birleştirir. Başlatan (Alice) karşı
// tarafın (Bob) yayımladığı prekey demetini alır; Bob daha sonra Alice'in ilk
// mesaj başlığındaki açık anahtarlarla AYNI sırrı türetir.
//
// Kripto matematiği @noble'da; burada yalnız protokolün akışı var. Anahtar
// şeması D-605: kimlik Ed25519, DH için X25519'a dönüştürülür; prekey'ler X25519.
import { ed25519, x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { base64ToBytes, bytesToBase64, utf8ToBytes, verifySignedPreKey } from './crypto';

const INFO = utf8ToBytes('nosubject-x3dh-1');

function concat(arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const a of arrays) { out.set(a, o); o += a.length; }
  return out;
}

// Signal X3DH KDF: eğriyi ayrıştırmak için 32 baytlık 0xFF öneki, sonra sıfır
// tuzlu HKDF-SHA256. DH'ler her iki tarafta AYNI sırada birleştirilmeli.
function deriveSharedKey(dhs: Uint8Array[]): Uint8Array {
  const prefix = new Uint8Array(32).fill(0xff);
  const km = concat([prefix, ...dhs]);
  return hkdf(sha256, km, new Uint8Array(32), INFO, 32);
}

// İlişkilendirilmiş veri (Signal): IK_A(başlatan) || IK_B(alıcı). İki taraf da
// aynı sırayla kurar; ilk şifreli mesajın kimlik doğrulamasına girer.
function associatedData(initiatorIdentity: Uint8Array, responderIdentity: Uint8Array): string {
  return bytesToBase64(concat([initiatorIdentity, responderIdentity]));
}

export type PeerBundle = {
  identityKey: string; // Ed25519 açık
  signedPreKey: { id: number; key: string; signature: string }; // X25519 açık + imza
  oneTimePreKey: { id: number; key: string } | null; // X25519 açık
};

export type InitiatorResult = {
  sharedSecret: string;
  ephemeralPublicKey: string;
  usedSignedPreKeyId: number;
  usedOneTimePreKeyId: number | null;
  associatedData: string;
};

// Başlatan tarafı: demeti doğrula, geçici anahtar üret, dört DH'yi hesapla, SK
// türet. Dönen `ephemeralPublicKey` ve kullanılan prekey kimlikleri Bob'a ilk
// mesajla gider ki o da aynı SK'yi kursun.
export function initiateSession(params: {
  myIdentityPublic: string;
  myIdentitySecret: string;
  peerBundle: PeerBundle;
}): InitiatorResult {
  const { peerBundle } = params;
  // İmzalanmamış bir prekey, araya giren birinin kendi anahtarını sokması
  // demektir; her şeyden önce imza doğrulanır.
  if (!verifySignedPreKey(peerBundle.identityKey, peerBundle.signedPreKey.key, peerBundle.signedPreKey.signature)) {
    throw new Error('İmzalı prekey doğrulanamadı');
  }
  const ephemeral = x25519.keygen();

  const ikA_secret = ed25519.utils.toMontgomerySecret(base64ToBytes(params.myIdentitySecret));
  const ikB_public = ed25519.utils.toMontgomery(base64ToBytes(peerBundle.identityKey));
  const spkB_public = base64ToBytes(peerBundle.signedPreKey.key);

  const dhs = [
    x25519.getSharedSecret(ikA_secret, spkB_public),        // DH1: IK_A ↔ SPK_B
    x25519.getSharedSecret(ephemeral.secretKey, ikB_public), // DH2: EK_A ↔ IK_B
    x25519.getSharedSecret(ephemeral.secretKey, spkB_public), // DH3: EK_A ↔ SPK_B
  ];
  if (peerBundle.oneTimePreKey) {
    dhs.push(x25519.getSharedSecret(ephemeral.secretKey, base64ToBytes(peerBundle.oneTimePreKey.key))); // DH4
  }

  return {
    sharedSecret: bytesToBase64(deriveSharedKey(dhs)),
    ephemeralPublicKey: bytesToBase64(ephemeral.publicKey),
    usedSignedPreKeyId: peerBundle.signedPreKey.id,
    usedOneTimePreKeyId: peerBundle.oneTimePreKey?.id ?? null,
    associatedData: associatedData(base64ToBytes(params.myIdentityPublic), base64ToBytes(peerBundle.identityKey)),
  };
}

// Alıcı tarafı: kendi özel anahtarları ve başlatanın açık anahtarlarıyla aynı
// dört DH'yi ters yönde hesaplar; sonuç aynı SK'dir. Kullanılan tek-kullanımlık
// prekey'in özeli çağırandan gelir (yoksa null).
export function respondSession(params: {
  myIdentityPublic: string;
  myIdentitySecret: string;
  mySignedPreKeySecret: string;
  myOneTimePreKeySecret: string | null;
  peerIdentityKey: string;   // Ed25519 açık (IK_A)
  peerEphemeralKey: string;  // X25519 açık (EK_A)
}): { sharedSecret: string; associatedData: string } {
  const ikB_secret = ed25519.utils.toMontgomerySecret(base64ToBytes(params.myIdentitySecret));
  const ikA_public = ed25519.utils.toMontgomery(base64ToBytes(params.peerIdentityKey));
  const spkB_secret = base64ToBytes(params.mySignedPreKeySecret);
  const ekA_public = base64ToBytes(params.peerEphemeralKey);

  const dhs = [
    x25519.getSharedSecret(spkB_secret, ikA_public),  // DH1: SPK_B ↔ IK_A
    x25519.getSharedSecret(ikB_secret, ekA_public),   // DH2: IK_B ↔ EK_A
    x25519.getSharedSecret(spkB_secret, ekA_public),  // DH3: SPK_B ↔ EK_A
  ];
  if (params.myOneTimePreKeySecret) {
    dhs.push(x25519.getSharedSecret(base64ToBytes(params.myOneTimePreKeySecret), ekA_public)); // DH4
  }

  return {
    sharedSecret: bytesToBase64(deriveSharedKey(dhs)),
    associatedData: associatedData(base64ToBytes(params.peerIdentityKey), base64ToBytes(params.myIdentityPublic)),
  };
}
